import { EmailLanguage, EmailFrequency } from './email';

export type EmailNotificationType =
  | 'global_report'
  | 'score_report'
  | 'competitor_news'
  | 'score_degradation'
  | 'tasks_recos'
  | 'traaker_news'
  | 'geo_news'
  | 'trial_lifecycle'
  | 'credit_alert'
  | 'audit_failed';

export type NotificationTriggerKind = 'scheduled' | 'event' | 'lifecycle' | 'broadcast';

/** Reuses EmailFrequency's values (daily/weekly/monthly) under the registry's own name. */
export type NotificationCadence = EmailFrequency;

export interface NotificationTypeDef {
  type: EmailNotificationType;
  trigger: NotificationTriggerKind;
  scope: 'brand' | 'tenant';
  visibleInSettings: boolean;
  cadences?: NotificationCadence[];
  defaultCadence?: NotificationCadence;
  defaultEnabled: boolean;
  /** Default true. audit_failed sets false — factual list, no Bedrock call (plan §14.2, D8). */
  aiCommentary?: boolean;
}

const SCHEDULED_CADENCES: NotificationCadence[] = ['daily', 'weekly', 'monthly'];

export const NOTIFICATION_REGISTRY: Record<EmailNotificationType, NotificationTypeDef> = {
  global_report: {
    type: 'global_report',
    trigger: 'scheduled',
    scope: 'brand',
    visibleInSettings: true,
    cadences: SCHEDULED_CADENCES,
    defaultCadence: 'weekly',
    defaultEnabled: false,
  },
  score_report: {
    type: 'score_report',
    trigger: 'scheduled',
    scope: 'brand',
    visibleInSettings: true,
    cadences: SCHEDULED_CADENCES,
    defaultCadence: 'weekly',
    defaultEnabled: false,
  },
  competitor_news: {
    type: 'competitor_news',
    trigger: 'scheduled',
    scope: 'brand',
    visibleInSettings: true,
    cadences: SCHEDULED_CADENCES,
    defaultCadence: 'weekly',
    defaultEnabled: false,
  },
  score_degradation: {
    type: 'score_degradation',
    trigger: 'event',
    scope: 'brand',
    visibleInSettings: true,
    defaultEnabled: false,
  },
  tasks_recos: {
    type: 'tasks_recos',
    trigger: 'scheduled',
    scope: 'brand',
    visibleInSettings: true,
    cadences: SCHEDULED_CADENCES,
    defaultCadence: 'weekly',
    defaultEnabled: false,
  },
  traaker_news: {
    type: 'traaker_news',
    trigger: 'broadcast',
    scope: 'tenant',
    // No backend yet (broadcasts are unbuilt, plan §3) — flip to true when the
    // admin-authored broadcast pipeline ships. Until then, NotificationsSettings
    // derives its card list from this field, so leaving it true here would show
    // a toggle for a type that can never actually fire.
    visibleInSettings: false,
    defaultEnabled: false,
  },
  geo_news: {
    type: 'geo_news',
    trigger: 'broadcast',
    scope: 'tenant',
    visibleInSettings: false,
    defaultEnabled: false,
  },
  trial_lifecycle: {
    type: 'trial_lifecycle',
    trigger: 'lifecycle',
    scope: 'tenant',
    visibleInSettings: false,
    defaultEnabled: true,
  },
  credit_alert: {
    type: 'credit_alert',
    trigger: 'event',
    scope: 'tenant',
    visibleInSettings: true,
    defaultEnabled: false,
  },
  audit_failed: {
    type: 'audit_failed',
    trigger: 'scheduled',
    scope: 'brand',
    visibleInSettings: true,
    cadences: SCHEDULED_CADENCES,
    defaultCadence: 'daily',
    defaultEnabled: false,
    aiCommentary: false,
  },
};

export interface NotificationRecipient {
  userId: string;
  language?: EmailLanguage;
  /** Per-recipient cadence override; falls back to the type-level cadence when unset. */
  cadence?: NotificationCadence;
}

export type DegradationScope =
  | 'website_overall'
  | 'bp_overall'
  | 'bp_axis'
  | 'bp_prompt'
  | 'earned_media_overall'
  | 'product_catalog_overall'
  | 'geo_visibility_measure';

export interface NotificationTypeConfig {
  enabled: boolean;
  cadence?: NotificationCadence;
  recipients: NotificationRecipient[];
  /**
   * Users who unsubscribed from a type whose recipients are IMPLICIT
   * (`trial_lifecycle`: the worker mails every validated tenant user, so there
   * is no `recipients` entry to drop). An explicit-recipient type expresses the
   * same thing by removing the entry from `recipients` and never sets this.
   */
  optedOutUserIds?: string[];
  channels?: { slack?: boolean; webhook?: boolean };
  degradation?: {
    thresholdPoints: number;
    consecutiveRuns: number;
    scopes: DegradationScope[];
  };
  creditAlert?: { thresholdCredits: number };
}

/** All `DegradationScope` values except `bp_prompt` (plan D3 — per-prompt defaults OFF, noise). */
export const DEFAULT_DEGRADATION_SCOPES: DegradationScope[] = [
  'website_overall',
  'bp_overall',
  'bp_axis',
  'earned_media_overall',
  'product_catalog_overall',
  'geo_visibility_measure',
];

/**
 * Plan D3 defaults for `score_degradation`/`credit_alert` sub-config. The
 * frontend and `EmailNotificationConfigService.updateType` both read these —
 * NOT independent `?? 5`/`?? 50` literals — so a user who enables a type with
 * no sub-config gets a rule that actually fires (fix for the 2026-08-21 audit
 * finding: evaluators bail out silently when the sub-config is absent).
 */
export const DEFAULT_DEGRADATION_CONFIG: NonNullable<NotificationTypeConfig['degradation']> = {
  thresholdPoints: 5,
  consecutiveRuns: 3,
  scopes: DEFAULT_DEGRADATION_SCOPES,
};

export const DEFAULT_CREDIT_ALERT_CONFIG: NonNullable<NotificationTypeConfig['creditAlert']> = {
  thresholdCredits: 50,
};

export const NOTIF_DUE_PK = 'NOTIF_DUE';
/** SK of the tenant's NOTIF_CONFIG row (PK = `TENANT#<id>`). */
export const NOTIF_CONFIG_SK = 'NOTIF_CONFIG';

/**
 * Tenant-level Slack integration (plan §13.2). One incoming-webhook URL per
 * tenant, toggled per type via `NotificationTypeConfig.channels.slack`.
 *
 * `webhookUrl` is a SECRET — possession of the URL is the only credential Slack
 * checks. It must never leave the API unmasked (`SlackConfigView`) and never
 * reach a log line.
 */
export interface SlackNotificationConfig {
  webhookUrl: string;
  addedByUserId: string;
  addedAt: string;
  /** Consecutive delivery failures; reset to 0 by the worker on any success. */
  failureCount?: number;
  /** Set by the worker after SLACK_BROKEN_AFTER consecutive failures (revoked URL). */
  broken?: boolean;
}

/** What the API returns instead of {@link SlackNotificationConfig}: no URL. */
export interface SlackConfigView {
  /** Domain + last 4, same masking idiom as `WebhookConfigView.secretHint`. */
  urlHint: string;
  addedByUserId: string;
  addedAt: string;
  broken?: boolean;
}

export interface TenantNotificationConfig {
  types: Partial<Record<EmailNotificationType, NotificationTypeConfig>>;
  slack?: SlackNotificationConfig;
  /** Sparse-GSI sort key (`notif-due-index`); present only while notifDuePK is set. */
  nextDueAt?: string;
  /** `${type}#${brandId}` (brand-scoped) or `${type}` (tenant-scoped) -> ISO timestamp. */
  lastSent?: Record<string, string>;
}

/** The client-safe projection of {@link TenantNotificationConfig}. */
export interface TenantNotificationConfigView extends Omit<TenantNotificationConfig, 'slack'> {
  slack?: SlackConfigView;
}

/** `https://hooks.slack.com/services/T…/B…/xxxx` → `hooks.slack.com/…xxxx`. */
export function maskSlackUrl(url: string): string {
  try {
    const { hostname } = new URL(url);
    return `${hostname}/…${url.slice(-4)}`;
  } catch {
    return `…${url.slice(-4)}`;
  }
}

export function toSlackConfigView(slack: SlackNotificationConfig): SlackConfigView {
  return {
    urlHint: maskSlackUrl(slack.webhookUrl),
    addedByUserId: slack.addedByUserId,
    addedAt: slack.addedAt,
    ...(slack.broken ? { broken: true } : {}),
  };
}

/**
 * Which notification types may fan out to non-email channels (plan §13.3):
 * broadcasts are marketing content and lifecycle is 1:1 onboarding copy — both
 * email-only. Drives which settings cards show channel toggles.
 */
export function supportsNonEmailChannels(type: EmailNotificationType): boolean {
  const trigger = NOTIFICATION_REGISTRY[type]?.trigger;
  return trigger === 'scheduled' || trigger === 'event';
}

export interface NotificationJob {
  /**
   * `render`   — scheduled type, sweep-dispatched, renderer table.
   * `event`    — hook-dispatched, evaluator table decides whether to send.
   * `lifecycle`— trial sequence (type 7). Bypasses NOTIF_CONFIG entirely (the
   *              type is `visibleInSettings: false` and a trial tenant typically
   *              has no config row at all); recipients are the tenant's own
   *              validated users. Email-only, always — see plan §13.3.
   */
  kind: 'render' | 'event' | 'lifecycle';
  type: EmailNotificationType;
  tenantId: string;
  brandId?: string;
  periodStart?: string;
  periodEnd?: string;
  event?: Record<string, unknown>;
  /** Which lifecycle step this job renders (`kind: 'lifecycle'` only). */
  step?: TrialLifecycleStep;
  triggeredFor: string;
}

/**
 * Trial-lifecycle sequence (type 7, plan §9). Five one-shot steps, driven by the
 * daily `reap-trials` cron — NOT by cadence and NOT by the sweep, which is why
 * `trial_lifecycle` is `trigger: 'lifecycle'` and invisible in settings.
 *
 * Order matters: `TRIAL_LIFECYCLE_STEPS` is walked in sequence and the FIRST due
 * step wins, so at most one lifecycle email per tenant per cron day.
 */
export type TrialLifecycleStep = 'e1_first_audit' | 'e2_recos' | 'e3_last_day' | 'e4_offer' | 'e5_winback';

export const TRIAL_LIFECYCLE_STEPS: TrialLifecycleStep[] = [
  'e1_first_audit',
  'e2_recos',
  'e3_last_day',
  'e4_offer',
  'e5_winback',
];

/**
 * Per-step send stamps, on `tenant.settings.trialLifecycle`. A step is sent at
 * most once ever: the stamp is the idempotency record AND the E2 clock (E2 fires
 * one day after E1's stamp, not after a fixed offset from signup — a tenant that
 * never runs an audit never gets E1, so never gets E2).
 */
export interface TrialLifecycleState {
  sent?: Partial<Record<TrialLifecycleStep, string>>;
  /** Set by the audit-finalize hook the first time any audit completes. */
  firstAuditCompletedAt?: string;
  /** Brand whose first audit fired E1 — the renderer scopes its summary to it. */
  firstAuditBrandId?: string;
}

/** The promotion code E4/E5 hand out. See docs/billing/pricing-and-billing.md §4.9. */
export const TRIAL_OFFER_PROMO_CODE = 'TRIAL50';

/**
 * Earliest nextDueAt across all enabled scheduled types (honoring per-recipient
 * cadence overrides — the tightest override wins so no recipient is under-served).
 * Returns undefined when no scheduled type is enabled (caller REMOVEs notifDuePK).
 */
export function computeNextDueAt(
  config: TenantNotificationConfig,
  registry: Record<EmailNotificationType, NotificationTypeDef>,
  now: Date,
): string | undefined {
  let earliest: number | undefined;

  for (const [type, typeConfig] of Object.entries(config.types) as Array<
    [EmailNotificationType, NotificationTypeConfig | undefined]
  >) {
    const def = registry[type];
    if (!def || def.trigger !== 'scheduled' || !typeConfig?.enabled) continue;

    for (const cadence of selectCadences(typeConfig, def)) {
      const dueMs = nextTickMs(cadence, now);
      if (earliest === undefined || dueMs < earliest) earliest = dueMs;
    }
  }

  return earliest === undefined ? undefined : new Date(earliest).toISOString();
}

/**
 * Whether one scheduled type (already known enabled) is due to fire right now,
 * anchored on ITS OWN last-send timestamp rather than the tenant-wide
 * `nextDueAt` — the fix for mixed-cadence tenants collapsing to the tightest
 * type (e.g. daily score_report firing the weekly global_report every day).
 * Never sent yet (`lastSentIso` undefined) is always due (first send).
 */
export function isScheduledTypeDue(
  typeConfig: NotificationTypeConfig,
  def: NotificationTypeDef,
  lastSentIso: string | undefined,
  now: Date,
): boolean {
  const cadence = tightestCadence(typeConfig, def);
  if (!cadence) return false;
  if (!lastSentIso) return true;
  return nextTickMs(cadence, new Date(lastSentIso)) <= now.getTime();
}

function selectCadences(
  typeConfig: NotificationTypeConfig,
  def: NotificationTypeDef,
): NotificationCadence[] {
  return (typeConfig.recipients.length
    ? typeConfig.recipients.map((r) => r.cadence ?? typeConfig.cadence ?? def.defaultCadence)
    : [typeConfig.cadence ?? def.defaultCadence]
  ).filter((c): c is NotificationCadence => !!c);
}

function tightestCadence(
  typeConfig: NotificationTypeConfig,
  def: NotificationTypeDef,
): NotificationCadence | undefined {
  const cadences = selectCadences(typeConfig, def);
  if (!cadences.length) return undefined;
  return cadences.reduce((tightest, c) =>
    CADENCE_INTERVAL_MS[c] < CADENCE_INTERVAL_MS[tightest] ? c : tightest,
  );
}

/**
 * Rolling intervals, not calendar-anchored (weekly ≠ next Monday, monthly ≠
 * the 1st) — a deliberate simplicity choice over the plan's original
 * calendar-anchoring language; see docs/features/email-notifications.md.
 */
const CADENCE_INTERVAL_MS: Record<NotificationCadence, number> = {
  daily: 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
  monthly: 30 * 24 * 60 * 60 * 1000,
};

function nextTickMs(cadence: NotificationCadence, now: Date): number {
  const interval = CADENCE_INTERVAL_MS[cadence];
  if (interval === undefined) {
    // Defense in depth: bad stored/patched data must fail loudly here, not
    // silently resolve to NaN → an invalid ISO string → the REMOVE branch that
    // strips notifDuePK and disables every scheduled type for the tenant.
    throw new Error(`Unknown notification cadence: ${cadence}`);
  }
  return now.getTime() + interval;
}
